// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { NumberInput, Slider, Stack, Switch, Text, Title, useMantineTheme } from '@mantine/core'
import { useDisclosure, useMediaQuery } from '@mantine/hooks'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { parseEther } from 'viem'

import RootButton from '@/components/Buttons/RootButton'
import Loading from '@/components/Loading'
import StakeCalculator from '@/components/Staking/StakeCalculator'
import WithdrawTable from '@/components/Tables/WithdrawTable'
import TooltipTitle from '@/components/TooltipTitle'
import TransactionSummary from '@/components/Transfer/TransactionSummary'
import { MIN_STAKE_DAYS, NATIVE_CHAIN_TICKER, STAKE_ADDRESS } from '@/constants/blockchain'
import { useEconomicsContext } from '@/contexts/EconomicsContext'
import { useStakingContext } from '@/contexts/StakingContext'
import StakeAbi from '@/contracts/Stake'
import { ContractCall } from '@/hooks/useChain'

import ModalSkeleton from '../../ModalSkeleton'
import RestakeConfirmation from '../Withdraw/RestakeConfirmation'
import WithdrawConfirmation from '../Withdraw/WithdrawConfirmation'

interface WithdrawProps {
  close: () => void
}

const Withdraw: React.FC<WithdrawProps> = ({ close }) => {
  const { t } = useTranslation()
  const theme = useMantineTheme()
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.md})`)

  // Get staking and economics data
  const { totalStakeSupply, stakeMaxDays, stakeMultiplier, rl1PerDay, getDayStart } =
    useEconomicsContext()
  const { staked, stake, stakedList, isLoading } = useStakingContext()

  // modal
  const [openedConfirmation, { open: openConfirmation, close: closeConfirmation }] = useDisclosure()
  // stake info
  const [stakeDays, setStakeDays] = useState<number>(MIN_STAKE_DAYS)

  const [selectedRows, setSelectedRows] = useState<number[]>([])
  const [selectedCompletedStakesAmount, setSelectedCompletedStakesAmount] = useState<string>('0')
  const [selectedCompletedStakesStake, setSelectedCompletedStakesStake] = useState<string>('0')

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isRestake, setIsRestake] = useState<boolean>(false)

  const contractCalls: ContractCall[] | undefined = useMemo(() => {
    if (!selectedCompletedStakesAmount) {
      return undefined
    }
    return [
      {
        args: {
          address: STAKE_ADDRESS,
          abi: StakeAbi,
          functionName: 'lock',
          args: [stakeDays],
          value: parseEther(selectedCompletedStakesAmount),
        },
      },
    ]
  }, [stakeDays, selectedCompletedStakesAmount])

  // Get finished stakes
  const finishedStakes = useMemo(
    () => stakedList?.filter(stakeItem => stakeItem.left < 0),
    [stakedList]
  )

  // Update values on selection
  useEffect(() => {
    if (!finishedStakes) return
    const selectedStakes = finishedStakes.filter(activeStake =>
      selectedRows.includes(activeStake.day)
    )
    const totalAmount = selectedStakes.reduce(
      (acc, activeStake) => acc + Number(activeStake.amount.fullValue),
      0
    )
    const totalStake = selectedStakes.reduce(
      (acc, activeStake) => acc + Number(activeStake.stake.fullValue),
      0
    )
    setSelectedCompletedStakesAmount(totalAmount.toString())
    setSelectedCompletedStakesStake(totalStake.toString())
  }, [finishedStakes, selectedRows])

  const handleClose = useCallback(() => {
    closeConfirmation()
    close()
  }, [closeConfirmation, close])

  return (
    <>
      <Stack style={{ maxWidth: '900px' }}>
        <Text>
          {
            t(
              'components.staking.staking.modal.manageDescription',
              'View your active stakes, their staking power multiplier, and how many days left to be able to withdraw. Use the checkboxes to select which stakes you would like to withdraw into useable L1.'
            ) as string
          }
        </Text>
        <Stack style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
          <Stack>
            <WithdrawTable
              isLoading={isLoading}
              activeStakes={stakedList}
              getDayStart={getDayStart}
              selectedRows={selectedRows}
              setSelectedRows={setSelectedRows}
            />
            {/* //! Switch to restake hidden for now */}
            {/* <Title order={4} style={{ marginTop: '20px' }}>
              {t('components.staking.staking.modal.stake.restakePeriod', 'Restaking Period')}
            </Title>
            <Switch
              checked={isRestake}
              onChange={() => setIsRestake(!isRestake)}
              label="Restake Withdrawal"
            /> */}
            {isRestake && (
              <>
                <Stack style={{ flexDirection: 'row', gap: 20, alignItems: 'center' }}>
                  <NumberInput
                    style={{ width: '90px' }}
                    allowDecimal={false}
                    value={stakeDays > 0 ? stakeDays : ''}
                    onChange={value => setStakeDays(Number(value))}
                    min={MIN_STAKE_DAYS}
                    max={stakeMaxDays}
                    step={1}
                    placeholder=""
                  />
                  <Slider
                    style={{ width: '100%', marginRight: 15 }}
                    value={stakeDays}
                    min={MIN_STAKE_DAYS}
                    max={stakeMaxDays}
                    step={1}
                    precision={1}
                    marks={[
                      { value: MIN_STAKE_DAYS, label: `${MIN_STAKE_DAYS}` },
                      { value: stakeMaxDays, label: `${stakeMaxDays}` },
                    ]}
                    label={(value: number) => `${value} days`}
                    onChange={setStakeDays}
                  />
                </Stack>
                <Text style={{ fontSize: 14, color: theme.colors.textAndIcon[0] }}>
                  {
                    t(
                      'components.staking.staking.modal.withdraw.withdrawStatus',
                      'Enter or use slider to select a staking period between 1 day and 10 years. '
                    ) as string
                  }
                </Text>
              </>
            )}
            {isMobile &&
              (staked && stake && totalStakeSupply ? (
                <StakeCalculator
                  amount={
                    isRestake
                      ? Number(selectedCompletedStakesAmount)
                      : -Number(selectedCompletedStakesAmount)
                  }
                  newStake={
                    isRestake
                      ? -Number(selectedCompletedStakesStake)
                      : -Number(selectedCompletedStakesStake)
                  }
                  stakeMaxDays={stakeMaxDays}
                  stakeMultiplier={stakeMultiplier}
                  currentStaked={staked}
                  currentStake={stake}
                  rl1PerDay={rl1PerDay}
                  stakeSupply={totalStakeSupply}
                  state={isRestake ? 'restake' : 'withdraw'}
                />
              ) : (
                <Loading />
              ))}
            <TooltipTitle
              title={t('components.staking.staking.modal.withdraw.total', 'Total to Withdraw')}
              tooltip={t(
                'components.staking.staking.modal.withdraw.completedStakesTooltip',
                'These tokens are liquid L1 that can be used to restake, create, or send to others on the network. They are no longer generating STAKE or RL1 rewards.'
              )}
            />
            <Stack
              style={{
                backgroundColor: theme.colors.background3[0],
                borderRadius: '12px',
                padding: '16px',
                width: 'fit-content',
                gap: 20,
              }}
            >
              <Title order={4}>
                {selectedCompletedStakesAmount} {NATIVE_CHAIN_TICKER}
              </Title>
            </Stack>
            <Stack style={{ flexDirection: 'row', marginTop: '20px' }}>
              <RootButton onClick={close} secondary style1={false} expand>
                {t('buttons.cancel', 'Cancel')}
              </RootButton>
              <RootButton
                disabled={
                  isRestake
                    ? !selectedCompletedStakesAmount || !stakeDays
                    : selectedCompletedStakesAmount === '0'
                }
                onClick={openConfirmation}
                style1={false}
                expand
              >
                {isRestake ? t('buttons.restake', 'Restake') : t('buttons.withdraw', 'Withdraw')}
              </RootButton>
            </Stack>
            <TransactionSummary contractCalls={isRestake ? contractCalls : undefined} />
          </Stack>
          {!isMobile &&
            (staked && stake && totalStakeSupply ? (
              <StakeCalculator
                amount={
                  isRestake
                    ? Number(selectedCompletedStakesAmount)
                    : -Number(selectedCompletedStakesAmount)
                }
                newStake={
                  isRestake
                    ? -Number(selectedCompletedStakesStake)
                    : -Number(selectedCompletedStakesStake)
                }
                stakeMaxDays={stakeMaxDays}
                stakeMultiplier={stakeMultiplier}
                currentStaked={staked}
                currentStake={stake}
                rl1PerDay={rl1PerDay}
                stakeSupply={totalStakeSupply}
                state={isRestake ? 'restake' : 'withdraw'}
              />
            ) : (
              <Loading />
            ))}
        </Stack>
      </Stack>

      <ModalSkeleton opened={openedConfirmation} onClose={closeConfirmation} title="Confirmation">
        {isRestake ? (
          <RestakeConfirmation
            selectedCompletedStakes={selectedCompletedStakesAmount}
            stakeCallConfig={
              contractCalls && contractCalls.length > 0 ? contractCalls[0].args : undefined
            }
            stakeDays={stakeDays}
            close={closeConfirmation}
          />
        ) : (
          <WithdrawConfirmation
            selectedDays={selectedRows}
            close={handleClose}
            cancel={closeConfirmation}
          />
        )}
      </ModalSkeleton>
    </>
  )
}

export default Withdraw
